import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import paymentService from '../../services/paymentService';

const initialState = {
  plans: [],
  selectedPlan: null,
  loading: false,
  error: null,
  registrationLoading: false,
  registrationError: null,
  paymentHistory: [],
  paymentSubscription: null,
  paymentHistoryLoading: false,
  subscriptionStatus: null,
  subscriptionStatusLoading: false,
  cancelLoading: false,
  reactivateLoading: false,
};

// Async thunk to fetch plans
export const fetchPlans = createAsyncThunk(
  'payment/fetchPlans',
  async (role, { rejectWithValue }) => {
    try {
      const data = await paymentService.getPlans(role);
      console.log(data);
      return data.plans || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch plans. Please try again.'
      );
    }
  }
);

// Async thunk to register with payment
export const registerWithPayment = createAsyncThunk(
  'payment/registerWithPayment',
  async (formData, { rejectWithValue }) => {
    try {
      const data = await paymentService.registerWithPayment(formData);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Registration failed. Please try again.'
      );
    }
  }
);

// Async thunk to fetch payment history
export const fetchPaymentHistory = createAsyncThunk(
  'payment/fetchPaymentHistory',
  async (_, { rejectWithValue }) => {
    try {
      const data = await paymentService.getPaymentHistory();
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch payment history.'
      );
    }
  }
);

// Async thunk to fetch subscription status
export const fetchSubscriptionStatus = createAsyncThunk(
  'payment/fetchSubscriptionStatus',
  async (_, { rejectWithValue }) => {
    try {
      const data = await paymentService.getSubscriptionStatus();
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch subscription status.'
      );
    }
  }
);

// Async thunk to cancel subscription
export const cancelSubscription = createAsyncThunk(
  'payment/cancelSubscription',
  async (_, { rejectWithValue }) => {
    try {
      const data = await paymentService.cancelSubscription();
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to cancel subscription.'
      );
    }
  }
);

// Async thunk to reactivate subscription
export const reactivateSubscription = createAsyncThunk(
  'payment/reactivateSubscription',
  async (_, { rejectWithValue }) => {
    try {
      const data = await paymentService.reactivateSubscription();
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to reactivate subscription.'
      );
    }
  }
);

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.registrationError = null;
    },
    setSelectedPlan: (state, action) => {
      state.selectedPlan = action.payload;
    },
    clearPaymentState: (state) => {
      state.plans = [];
      state.selectedPlan = null;
      state.loading = false;
      state.error = null;
      state.registrationLoading = false;
      state.registrationError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Plans
      .addCase(fetchPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload;
        state.error = null;
      })
      .addCase(fetchPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Register with Payment
      .addCase(registerWithPayment.pending, (state) => {
        state.registrationLoading = true;
        state.registrationError = null;
      })
      .addCase(registerWithPayment.fulfilled, (state) => {
        state.registrationLoading = false;
        state.registrationError = null;
      })
      .addCase(registerWithPayment.rejected, (state, action) => {
        state.registrationLoading = false;
        state.registrationError = action.payload;
      })
      // Fetch Payment History
      .addCase(fetchPaymentHistory.pending, (state) => {
        state.paymentHistoryLoading = true;
        state.error = null;
      })
      .addCase(fetchPaymentHistory.fulfilled, (state, action) => {
        state.paymentHistoryLoading = false;
        state.paymentHistory = action.payload.payments || [];
        state.paymentSubscription = action.payload.subscription || null;
        state.error = null;
      })
      .addCase(fetchPaymentHistory.rejected, (state, action) => {
        state.paymentHistoryLoading = false;
        state.error = action.payload;
      })
      // Fetch Subscription Status
      .addCase(fetchSubscriptionStatus.pending, (state) => {
        state.subscriptionStatusLoading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionStatus.fulfilled, (state, action) => {
        state.subscriptionStatusLoading = false;
        state.subscriptionStatus = action.payload;
        state.error = null;
      })
      .addCase(fetchSubscriptionStatus.rejected, (state, action) => {
        state.subscriptionStatusLoading = false;
        state.error = action.payload;
      })
      // Cancel Subscription
      .addCase(cancelSubscription.pending, (state) => {
        state.cancelLoading = true;
        state.error = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.cancelLoading = false;
        if (action.payload.subscription) {
          state.subscriptionStatus = action.payload;
        }
        state.error = null;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.cancelLoading = false;
        state.error = action.payload;
      })
      // Reactivate Subscription
      .addCase(reactivateSubscription.pending, (state) => {
        state.reactivateLoading = true;
        state.error = null;
      })
      .addCase(reactivateSubscription.fulfilled, (state, action) => {
        state.reactivateLoading = false;
        // Update subscription status if available
        if (action.payload.subscription) {
          state.subscriptionStatus = action.payload;
        }
        state.error = null;
      })
      .addCase(reactivateSubscription.rejected, (state, action) => {
        state.reactivateLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setSelectedPlan, clearPaymentState } = paymentSlice.actions;
export default paymentSlice.reducer;
